import { useEffect, useRef, useState } from "react";
import { api, API, fmtPeso, fmtNum, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, UploadCloud, FileSpreadsheet, Download, Loader2, Info } from "lucide-react";
import MexicanaFacturas from "@/pages/MexicanaFacturas";

const FIELDS = [
  { key: "numero", label: "Número", type: "text" },
  { key: "fecha", label: "Fecha", type: "date" },
  { key: "pedimento", label: "Pedimento", type: "text" },
  { key: "tipo_cambio", label: "Tipo de Cambio", type: "number", disp: "rate" },
  { key: "num_pipas", label: "Núm. de Pipas", type: "number", disp: "count" },
  { key: "cuota_validacion", label: "Cuota de Validación", type: "number", disp: "money" },
  { key: "honorarios", label: "Honorarios", type: "number", disp: "money" },
  { key: "contraprestacion", label: "Contraprestación", type: "number", disp: "money" },
  { key: "servicio_extraordinario", label: "Servicio Extraordinario", type: "number", disp: "money" },
  { key: "dia_festivo", label: "Día Festivo", type: "number", disp: "money" },
  { key: "manifestacion_valor", label: "Manifestación de Valor", type: "number", disp: "money" },
  { key: "iva", label: "I.V.A.", type: "number", disp: "money" },
  { key: "subtotal", label: "Subtotal", type: "number", disp: "money" },
  { key: "multa", label: "Multa", type: "number", disp: "money" },
  { key: "total", label: "Total", type: "number", disp: "money" },
  { key: "factura", label: "Factura", type: "text" },
];
const NUM_KEYS = FIELDS.filter((f) => f.type === "number").map((f) => f.key);
const blank = () => Object.fromEntries(FIELDS.map((f) => [f.key, ""]));
const toPayload = (form) => {
  const p = { ...form };
  NUM_KEYS.forEach((k) => { p[k] = parseFloat(form[k]) || 0; });
  return p;
};
const dispCell = (f, v) => {
  if (f.type === "date") return fmtDate(v);
  if (f.disp === "money") return fmtPeso(v);
  if (f.disp === "rate") return fmtNum(v);
  if (f.disp === "count") return Number(v || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 });
  return v || "—";
};

export default function AgenciaMexicana() {
  const { can, activeCompanyId } = useAuth();
  const canEdit = can("aduanas", "edit");
  const canDelete = can("aduanas", "delete");
  const [tab, setTab] = useState("captura");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ fecha_agencia: "", fecha: "", pedimento: "", factura: "" });
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blank());
  const [showImport, setShowImport] = useState(false);
  const [impFecha, setImpFecha] = useState("");
  const [impFile, setImpFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [impResult, setImpResult] = useState(null);
  const impRef = useRef();
  const [selected, setSelected] = useState(new Set());
  const token = localStorage.getItem("token");

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get(`/aduanas/mexicana?${p.toString()}`).then((r) => { setRows(r.data); setSelected(new Set()); }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters]);

  const openNew = () => { setEditId(null); setForm(blank()); setShowForm(true); };
  const openEdit = (r) => { setEditId(r.id); setForm({ ...blank(), ...r }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/aduanas/mexicana/${editId}`, toPayload(form));
      else await api.post("/aduanas/mexicana", toPayload(form));
      toast.success(editId ? "Registro actualizado" : "Registro agregado");
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("¿Eliminar registro?")) return; await api.delete(`/aduanas/mexicana/${id}`); toast.success("Eliminado"); load(); };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selected.size} registro(s) seleccionado(s)?`)) return;
    try {
      const r = await api.post("/aduanas/mexicana/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${r.data.eliminados} registro(s) eliminado(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doImport = async () => {
    if (!impFecha) { toast.error("La Fecha Agencia es obligatoria"); return; }
    if (!impFile) { toast.error("Selecciona un archivo Excel"); return; }
    setImporting(true); setImpResult(null);
    try {
      const fd = new FormData();
      fd.append("fecha_agencia", impFecha);
      fd.append("file", impFile);
      const r = await api.post("/aduanas/mexicana/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImpResult(r.data);
      toast.success(`${r.data.importados} importados · ${r.data.con_error} con error`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const dl = (path) => {
    const sep = path.includes("?") ? "&" : "?";
    const a = document.createElement("a");
    a.href = `${API}${path}${sep}auth=${token}`;
    a.download = ""; document.body.appendChild(a); a.click(); a.remove();
  };
  const exportXlsx = () => {
    const p = new URLSearchParams({ cid: activeCompanyId });
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    dl(`/aduanas/mexicana/export?${p.toString()}`);
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  const tabsBar = (
    <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1">
      <button data-testid="mex-tab-captura" onClick={() => setTab("captura")}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "captura" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Captura
      </button>
      <button data-testid="mex-tab-facturas" onClick={() => setTab("facturas")}
        className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${tab === "facturas" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
        Facturas (XML/PDF)
      </button>
    </div>
  );

  if (tab === "facturas") {
    return (
      <div className="space-y-5">
        {tabsBar}
        <MexicanaFacturas />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {tabsBar}
      {/* Acciones */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && <button data-testid="mex-add" onClick={openNew} className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2"><Plus className="h-4 w-4" /> Agregar Registro</button>}
          {canEdit && <button data-testid="mex-import" onClick={() => { setShowImport(true); setImpResult(null); setImpFecha(""); setImpFile(null); }} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><UploadCloud className="h-4 w-4" /> Importar Excel</button>}
          {canDelete && selected.size > 0 && <button data-testid="mex-bulk-delete" onClick={bulkDelete} className="bg-red-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-red-700 flex items-center gap-2"><Trash2 className="h-4 w-4" /> Eliminar seleccionados ({selected.size})</button>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="mex-plantilla" onClick={() => dl("/aduanas/mexicana/plantilla")} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"><Download className="h-4 w-4" /> Plantilla</button>
          <button data-testid="mex-export" onClick={exportXlsx} className="bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-700 flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Exportar Excel</button>
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-md p-3">
        <div><label className="text-xs text-slate-500">Fecha Agencia</label><input data-testid="mex-f-fecha-agencia" type="date" className={input} value={filters.fecha_agencia} onChange={(e) => setFilters({ ...filters, fecha_agencia: e.target.value })} /></div>
        <div><label className="text-xs text-slate-500">Fecha</label><input data-testid="mex-f-fecha" type="date" className={input} value={filters.fecha} onChange={(e) => setFilters({ ...filters, fecha: e.target.value })} /></div>
        <div><label className="text-xs text-slate-500">Pedimento</label><input data-testid="mex-f-pedimento" className={input} value={filters.pedimento} onChange={(e) => setFilters({ ...filters, pedimento: e.target.value })} /></div>
        <div><label className="text-xs text-slate-500">Factura</label><input data-testid="mex-f-factura" className={input} value={filters.factura} onChange={(e) => setFilters({ ...filters, factura: e.target.value })} /></div>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-slate-50 text-slate-500 border-y border-slate-200">
            <tr>
              {canDelete && <th className="px-3 py-3 w-10"><input data-testid="mex-select-all" type="checkbox" checked={allChecked} onChange={toggleAll} className="rounded border-slate-300" /></th>}
              <th className="px-3 py-3 font-medium text-left">Fecha Agencia</th>
              {FIELDS.map((f) => <th key={f.key} className={`px-3 py-3 font-medium ${f.type === "number" ? "text-right" : "text-left"}`}>{f.label}</th>)}
              {(canEdit || canDelete) && <th className="px-3 py-3" />}
            </tr>
          </thead>
          <tbody data-testid="mex-table">
            {loading && <tr><td colSpan={19} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={19} className="px-4 py-10 text-center text-slate-400">Sin registros</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                {canDelete && <td className="px-3 py-2.5"><input data-testid={`mex-select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="rounded border-slate-300" /></td>}
                <td className="px-3 py-2.5 text-slate-900 font-medium">{fmtDate(r.fecha_agencia)}</td>
                {FIELDS.map((f) => (
                  <td key={f.key} className={`px-3 py-2.5 ${f.type === "number" ? "text-right tabular-nums" : "text-slate-600"}`}>
                    {dispCell(f, r[f.key])}
                  </td>
                ))}
                {(canEdit || canDelete) && (
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {canEdit && <button data-testid={`mex-edit-${r.id}`} onClick={() => openEdit(r)} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4" /></button>}
                    {canDelete && <button data-testid={`mex-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form manual / edición */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mex-form-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">{editId ? "Editar registro" : "Agregar registro"}</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={save} className="p-6 grid grid-cols-2 md:grid-cols-3 gap-3">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-xs text-slate-500">{f.label}</label>
                  <input data-testid={`mex-field-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} step={f.type === "number" ? "any" : undefined}
                    className={input} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                </div>
              ))}
              <div className="col-span-2 md:col-span-3 flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
                <Info className="h-3.5 w-3.5" /> {editId ? `Fecha Agencia: ${form.fecha_agencia} (no editable)` : "La Fecha Agencia se asignará automáticamente con la fecha de creación."}
              </div>
              <div className="col-span-2 md:col-span-3 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="border border-slate-300 rounded-md px-4 py-2 text-sm hover:bg-slate-50">Cancelar</button>
                <button data-testid="mex-save" className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Importar */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowImport(false); }}>
          <div className="bg-white rounded-md shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mex-import-dialog">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Importar Excel</h3>
              <button onClick={() => setShowImport(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs text-slate-500">Fecha Agencia (obligatoria) — se asigna a todos los registros importados</label>
                <input data-testid="mex-import-fecha" type="date" className={input} value={impFecha} onChange={(e) => setImpFecha(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Archivo Excel (.xlsx)</label>
                <input ref={impRef} data-testid="mex-import-file" type="file" accept=".xlsx" className={input} onChange={(e) => setImpFile(e.target.files?.[0] || null)} />
              </div>
              <button data-testid="mex-import-run" onClick={doImport} disabled={importing} className="w-full bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2 disabled:opacity-50">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Procesar importación
              </button>

              {impResult && (
                <div className="border border-slate-200 rounded-md p-4 space-y-2" data-testid="mex-import-summary">
                  <div className="grid grid-cols-3 gap-2 text-center text-sm">
                    <div className="bg-slate-50 rounded p-2"><div className="font-bold text-slate-900">{impResult.procesados}</div><div className="text-xs text-slate-500">Procesados</div></div>
                    <div className="bg-emerald-50 rounded p-2"><div className="font-bold text-emerald-700">{impResult.importados}</div><div className="text-xs text-slate-500">Importados</div></div>
                    <div className="bg-red-50 rounded p-2"><div className="font-bold text-red-600">{impResult.con_error}</div><div className="text-xs text-slate-500">Con error</div></div>
                  </div>
                  {impResult.errores?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto text-xs border-t border-slate-100 pt-2">
                      {impResult.errores.map((e, i) => (
                        <div key={`${e.fila}-${e.campo}-${i}`} className="text-red-600 py-0.5">Fila {e.fila} · <b>{e.campo}</b>: {e.descripcion}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
